import { LitElement, css, html, nothing, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import './bb-json'
import sheet from './css/bb-log.css?inline'
import { type LogEntry, statusTone, summariseLog } from './bbcore/log'

// Parse `s` as JSON. Returns `{ ok: true, value }` on success, `null`
// on failure — used to decide whether to render a body as a collapsible
// tree (<bb-json>) or as a plain text block.
function tryParseJson(s: string | undefined): { value: unknown } | null {
  if (!s) return null
  try {
    return { value: JSON.parse(s) }
  } catch {
    return null
  }
}

// A read-only activity feed: every relay call, every raw bot hint, and a few
// client-side milestones, in the order they happened. Mirrors demo-dev's
// "Show system messages" view. The widget owns the entry list and pushes it
// down via `.entries`; the panel only renders and (optionally) asks the
// widget to clear it via a bubbled `log-clear` event.

@customElement('bb-log')
export class BbLog extends LitElement {
  static styles = css`${unsafeCSS(sheet)}`

  @property({ attribute: false })
  entries: LogEntry[] = []

  @state() private expanded = new Set<number>()

  updated() {
    // Keep the newest entry in view.
    const body = this.renderRoot.querySelector('.body') as HTMLElement | null
    if (body) body.scrollTop = body.scrollHeight
  }

  private toggle(id: number) {
    const next = new Set(this.expanded)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    this.expanded = next
  }

  private clear() {
    this.dispatchEvent(
      new CustomEvent('log-clear', { bubbles: true, composed: true }),
    )
  }

  private renderDetail(e: LogEntry): unknown {
    if (e.kind === 'hint') {
      return html`<bb-json .value=${e.hint}></bb-json>`
    }
    if (e.kind === 'http') {
      const req = tryParseJson(e.requestBody)
      const res = tryParseJson(e.responseBody)
      const rawReq = e.requestBody && !req
        ? html`<pre class="raw">${e.requestBody}</pre>`
        : nothing
      const rawRes = e.responseBody && !res
        ? html`<pre class="raw">${e.responseBody}</pre>`
        : nothing
      return html`
        ${e.requestBody
          ? html`<div class="section-label">→ request</div>
              ${req ? html`<bb-json .value=${req.value}></bb-json>` : rawReq}`
          : nothing}
        ${e.responseBody
          ? html`<div class="section-label">← response</div>
              ${res ? html`<bb-json .value=${res.value}></bb-json>` : rawRes}`
          : nothing}
      `
    }
    return null
  }

  private hasDetail(e: LogEntry): boolean {
    if (e.kind === 'hint') return true
    if (e.kind === 'http') return Boolean(e.requestBody || e.responseBody)
    return false
  }

  private renderEntry(e: LogEntry) {
    const kindClass = e.kind === 'http' ? `http ${statusTone(e.status)}` : e.kind
    const expandable = this.hasDetail(e)
    const isOpen = this.expanded.has(e.id)
    const label = e.kind === 'http' ? e.method : e.kind
    return html`
      <div
        class="entry ${kindClass}${expandable ? ' expandable' : ''}"
        @click=${() => expandable && this.toggle(e.id)}
      >
        <span class="label">${label}</span>${summariseLog(e)}${expandable
          ? html`<span class="caret">${isOpen ? '▾' : '▸'}</span>`
          : nothing}
        ${expandable && isOpen
          ? html`<div
              class="detail"
              @click=${(ev: Event) => ev.stopPropagation()}
            >
              ${this.renderDetail(e)}
            </div>`
          : nothing}
      </div>
    `
  }

  render() {
    return html`
      <div class="header">
        <span>Activity · ${this.entries.length}</span>
        <button
          class="clear"
          ?disabled=${this.entries.length === 0}
          @click=${this.clear}
        >
          clear
        </button>
      </div>
      <div class="body">
        ${this.entries.length === 0
          ? html`<div class="empty">No activity yet</div>`
          : this.entries.map((e) => this.renderEntry(e))}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'bb-log': BbLog
  }
}
